<template>
    <form-group :horizontal="horizontal" :label="label" :label-classes="[{ required }]">
        <dropdown
            :class="{
                'is-invalid': invalidFeedback,
            }"
            data-testid="hierarchical-multi-select"
            :disabled="disabled"
            :popper="false"
            :toggler-text="selectedDisplayText"
            :visible="show"
            auto-close="outside"
            class="form-control dropdown dropdown-multiline border-0 p-0"
            color="secondary"
            @update:show="handleDropdownVisibilityChange"
        >
            <template #toggler>
                <div v-if="disabled" class="absolute inset-0 z-[1]" @click.prevent="" />
                <div class="w-full">
                    <location-selection-display :placeholder="placeholder" :selection-objects="selectionDisplayObjects" />
                </div>
                <spinner v-if="isProcessing" class="absolute right-1" color="white" />
            </template>
            <div ref="listWrapper" class="dropdown-list p-0">
                <div v-if="showSearchbar" class="sticky top-0 z-[2] border-b border-gray-200 bg-white p-2">
                    <div class="flex min-w-[200px] rounded-xl border border-gray-200 border-gray-300 bg-white p-2 focus-within:!border-primary">
                        <input
                            ref="listFilter"
                            v-model="searchString"
                            :placeholder="searchPlaceholder"
                            autofocus
                            class="flex-1 rounded-l-md pr-2 focus:outline-none"
                            @focus="$refs.listFilter?.select()"
                        />
                        <button
                            class="flex items-center justify-center rounded-r-md text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none"
                            type="button"
                            @click="listFilter?.focus()"
                        >
                            <spinner v-if="isProcessing" />
                            <search v-else />
                        </button>
                    </div>
                </div>
                <div>
                    <!-- No results message -->
                    <div v-if="hasNoSearchResults" class="p-4 text-center text-gray-500">
                        <div class="text-sm">
                            {{ noResultsText }}
                        </div>
                        <div class="mt-1 text-xs text-gray-400">
                            {{ noResultsHintText }}
                        </div>
                    </div>

                    <!-- Hierarchy items -->
                    <hierarchy-node
                        v-for="item in filteredHierarchy"
                        :key="item.id"
                        :collapsed-items="collapsedItems"
                        :get-selection-state="getSelectionStateForItem"
                        :is-searching="Boolean(debouncedSearchString)"
                        :item="item"
                        :level="0"
                        :selected="selected"
                        @toggle-option="toggleOption"
                        @toggle-collapse="toggleCollapse"
                    />
                </div>
            </div>
            <div v-if="$slots.actions" class="dropdown-actions p-0">
                <hr class="dropdown-divider m-0" />
                <div class="p-2">
                    <slot name="actions" />
                </div>
            </div>
        </dropdown>
        <div v-if="invalidFeedback" class="invalid-feedback">
            {{ invalidFeedback }}
        </div>
    </form-group>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { debounce } from 'lodash';

import Spinner from '@/Components/Spinner.vue';
import Search from '@/../icons/Search.vue';

import HierarchyNode from '@/Components/HierarchyNode.vue';
import FormGroup from '@/Components/FormGroup.vue';
import Dropdown from '@/Components/Dropdown.vue';
import LocationSelectionDisplay from '@/Components/LocationSelectionDisplay.vue';
import { trans } from '@/mixins/i18n';
import { buildLocationDisplayObjects, getAllDescendantIds, getSelectionState } from '@/composables/useHierarchicalLocationDisplay.ts';

// Props
const props = defineProps({
    label: String,
    name: String,
    hierarchicalOptions: Array, // Expect hierarchical structure
    modelValue: [String, Array],
    placeholder: {
        type: String,
        default: () => trans('general.form.select.placeholder'),
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    showSearchbar: {
        type: Boolean,
        default: false,
    },
    isProcessing: {
        type: Boolean,
        default: false,
    },
    invalidFeedback: String,
    horizontal: {
        type: Boolean,
        default: true,
    },
    required: {
        type: Boolean,
        default: false,
    },
    // Search/empty-state copy and the searchable node fields default to the
    // location picker's, so existing consumers are unchanged; other trees (e.g.
    // maintainer contacts) override them.
    searchPlaceholder: {
        type: String,
        default: () => trans('users.edit_user.field.locations.search.placeholder'),
    },
    noResultsText: {
        type: String,
        default: () => trans('users.edit_user.field.locations.search.no_results'),
    },
    noResultsHintText: {
        type: String,
        default: () => trans('users.edit_user.field.locations.search.try_different_keywords'),
    },
    searchKeys: {
        type: Array,
        default: () => ['name', 'iataCode', 'icaoCode'],
    },
    // Optional custom chip label builder forwarded to buildLocationDisplayObjects.
    displayNameBuilder: {
        type: Function,
        default: null,
    },
});

const emit = defineEmits(['update:modelValue']);

const show = ref(false);
const selected = ref([]);
const searchString = ref('');
const debouncedSearchString = ref('');
const collapsedItems = ref(new Set());

const hasUserInteracted = ref(false);
const initialModelValues = ref([]);
const hasCapturedInitialModel = ref(false);

const listFilter = ref(null);
const listWrapper = ref(null);

const selectedOptions = computed(() => {
    const flatItems = [];
    const flatten = (items) => {
        for (const item of items) {
            flatItems.push(item);
            if (item.children) flatten(item.children);
        }
    };
    flatten(hierarchyWithVisibility.value);
    return selected.value?.map((value) => flatItems.find((item) => item.id === value)).filter(Boolean) || [];
});

const filteredHierarchy = computed(() => {
    return hierarchyWithVisibility.value;
});

const hasNoSearchResults = computed(() => {
    // Only show no results when actively searching
    if (!debouncedSearchString.value) return false;

    // Check if any items are visible in the hierarchy
    const hasVisibleItems = (items) => {
        return items.some((item) => item._isVisible || (item.children && hasVisibleItems(item.children)));
    };

    return !hasVisibleItems(filteredHierarchy.value);
});

const selectedDisplayText = computed(() => {
    if (selectedOptions.value.length === 0) {
        return props.placeholder;
    }
    return `${selectedOptions.value.length} ${trans('general.selection.items_selected')}`;
});

const selectionDisplayObjects = computed(() => {
    return buildLocationDisplayObjects(props.hierarchicalOptions, selected.value, {
        includeMetadata: false,
        displayNameBuilder: props.displayNameBuilder ?? undefined,
    });
});

const itemsWithSearchableText = computed(() => {
    // Children inherit their ancestors' searchable text, so a match on a parent
    // (e.g. a maintainer name) keeps its leaves visible in search mode instead of
    // hiding the contacts the user is trying to pick.
    const processItem = (item, ancestorSearchableText = '') => {
        const ownSearchableText = props.searchKeys
            .map((key) => item[key] || '')
            .join(' ')
            .toLowerCase();
        const searchableText = `${ancestorSearchableText} ${ownSearchableText}`.trim();

        return {
            ...item,
            _searchableText: searchableText,
            children: item.children ? item.children.map((child) => processItem(child, searchableText)) : undefined,
        };
    };

    return (props.hierarchicalOptions || []).map((item) => processItem(item));
});

/**
 * Pre-computes visibility states for all nodes in a single tree traversal.
 */
const hierarchyWithVisibility = computed(() => {
    const searchLower = debouncedSearchString.value;
    const hasSearch = Boolean(searchLower);

    const processItem = (item) => {
        // Check if this item matches the search
        const itemSearchableText = item._searchableText || '';
        const itemMatches = !hasSearch || itemSearchableText.includes(searchLower);

        // Process children first to determine descendant matches
        const processedChildren = item.children ? item.children.map(processItem) : [];

        // Check if any descendant is visible
        const hasVisibleDescendant = processedChildren.some((child) => child._isVisible);

        // Item is visible if it matches or has visible descendants
        const isVisible = itemMatches || hasVisibleDescendant;

        // Children should be shown if:
        // 1. No search: respect collapse state
        // 2. With search: show if any descendant matches
        const shouldShowChildren = processedChildren.length > 0 && (hasSearch ? hasVisibleDescendant : !collapsedItems.value.has(item.id));

        return {
            ...item,
            children: processedChildren,
            _isVisible: isVisible,
            _shouldShowChildren: shouldShowChildren,
        };
    };

    return itemsWithSearchableText.value.map(processItem);
});

const updateDebouncedSearch = debounce((value) => {
    debouncedSearchString.value = value.toLowerCase();
}, 300);

const arraysEqual = (a, b) => {
    const setA = new Set(a);
    const setB = new Set(b);
    return setA.size === setB.size && [...setA].every((v) => setB.has(v));
};

const findItemById = (id) => {
    const search = (items) => {
        for (const item of items) {
            if (item.id === id) return item;
            if (item.children) {
                const found = search(item.children);
                if (found) return found;
            }
        }
        return null;
    };
    return search(props.hierarchicalOptions || []);
};
const findParent = (targetId) => {
    const search = (items) => {
        for (const item of items) {
            if (item.children) {
                for (const child of item.children) {
                    if (child.id === targetId) return item;
                    const found = search([child]);
                    if (found) return found;
                }
            }
        }
        return null;
    };
    return search(props.hierarchicalOptions || []);
};

const addItemFromSelection = (selection, item) => {
    const ids = [item.id, ...getAllDescendantIds(item)];
    let newSelection = [...new Set([...selection, ...ids])];

    newSelection = addAncestorsWithFullySelectedDescendants(newSelection, item);

    return newSelection;
};

const updateAncestorsBasedOnDescendants = (selection, item, shouldIncludeParent) => {
    let currentSelection = [...selection];
    let currentItem = item;

    while (true) {
        const parent = findParent(currentItem.id);
        if (!parent) break;

        const descendants = getAllDescendantIds(parent);
        const include = shouldIncludeParent(descendants, currentSelection, parent.id);

        if (include !== currentSelection.includes(parent.id)) {
            currentSelection = include ? [...currentSelection, parent.id] : currentSelection.filter((id) => id !== parent.id);
            currentItem = parent;
        } else {
            break;
        }
    }

    return currentSelection;
};

const addAncestorsWithFullySelectedDescendants = (selection, item) => {
    return updateAncestorsBasedOnDescendants(selection, item, (descendants, currentSelection) =>
        descendants.every((id) => currentSelection.includes(id)),
    );
};

const removeItemFromSelection = (selection, item) => {
    const idsToRemove = new Set([item.id, ...getAllDescendantIds(item)]);
    let newSelection = selection.filter((id) => !idsToRemove.has(id));

    return updateAncestorsBasedOnDescendants(newSelection, item, (descendants, currentSelection) =>
        descendants.some((id) => currentSelection.includes(id)),
    );
};

const normalizePreselectedValues = (values) => {
    let normalized = [];
    for (const id of values) {
        const item = findItemById(id);
        if (item) {
            normalized = addItemFromSelection(normalized, item);
        }
    }
    return normalized;
};

const setSelectedFromModel = () => {
    const values = Array.isArray(props.modelValue) ? props.modelValue : props.modelValue == null ? [] : [props.modelValue];
    const coerced = values;
    selected.value = coerced;
    if (!hasUserInteracted.value) {
        initialModelValues.value = [...coerced];
        hasCapturedInitialModel.value = true;
    }
};

/**
 * Expands parent selections to include all descendant IDs for hierarchical consistency.
 * Prevents UI state mismatches when initial values only contain parent IDs.
 */
const attemptNormalization = () => {
    if (hasUserInteracted.value) return;
    if (!Array.isArray(props.hierarchicalOptions) || props.hierarchicalOptions.length === 0) return;

    const baseValues = Array.isArray(initialModelValues.value) ? initialModelValues.value : [];
    const normalized = normalizePreselectedValues(baseValues);
    if (!arraysEqual(normalized, selected.value)) {
        selected.value = normalized;
    }
    if (!arraysEqual(normalized, props.modelValue)) {
        emit('update:modelValue', normalized);
    }
};

const handleDropdownVisibilityChange = (isShown) => {
    show.value = isShown;

    if (isShown) {
        nextTick(() => {
            listFilter.value?.focus();
        });
    } else {
        searchString.value = '';
    }
};

const toggleOption = (value) => {
    hasUserInteracted.value = true;
    const item = findItemById(value);
    if (!item) return;

    const selection = selected.value.includes(value) ? removeItemFromSelection(selected.value, item) : addItemFromSelection(selected.value, item);

    emit('update:modelValue', selection);
};

const toggleCollapse = (itemId) => {
    if (collapsedItems.value.has(itemId)) {
        collapsedItems.value.delete(itemId);
    } else {
        collapsedItems.value.add(itemId);
    }
};

const getSelectionStateForItem = (item) => {
    return getSelectionState(item, selected.value);
};

watch(
    () => props.modelValue,
    () => {
        setSelectedFromModel();
        if (!hasUserInteracted.value) {
            attemptNormalization();
        }
    },
);

watch(
    () => props.hierarchicalOptions,
    () => {
        if (!hasUserInteracted.value) {
            attemptNormalization();
        }
    },
    { deep: true },
);

watch(
    searchString,
    (newValue) => {
        updateDebouncedSearch(newValue);
    },
    { immediate: true },
);

onMounted(() => {
    setSelectedFromModel();
    attemptNormalization();
});

onBeforeUnmount(() => {
    updateDebouncedSearch.cancel();
});
</script>
